import React, { useEffect, useReducer, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useFirestoreConnect, isLoaded } from "react-redux-firebase";
import { useSelector } from "react-redux";
import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Heading,
  HStack,
  Image,
  Pressable,
  Spinner,
  Text,
  VStack,
} from "native-base";
import { DishModal } from "../components/venue/DishModal";
import { CartSheet } from "../components/venue/CartSheet";
import SEO from "../components/SEO";
import { OrderInApp } from "../components/venue/OrderInApp";
import { VenueDetails } from "../components/venue/VenueDetails";
import { estimatedPriceText } from "../utils/venueDetails";
import { hasDishAccessibility } from "../components/venue/DishAccessibilityInfo";
import { WEB_ORDERING_ENABLED, canTakePayments } from "../orderingConfig";
import { orderMenusForDisplay, sortMenus } from "../utils/menuSort";
import { attachMenuAccessibility } from "../utils/menuAccessibility";
import { getOpeningStatus, getWeeklyHours } from "../utils/openingHours";
import {
  CONFIDENCE_LABELS,
  STATUS_LABELS,
  buildAccessibilityCategories,
  hasVenueEnteredAccessibility,
  overallConfidence,
} from "../utils/venueAccessibility";

// ─────────────────────────────────────────────────────────────────
// Cart state (useReducer)
// ─────────────────────────────────────────────────────────────────

const cartReducer = (state, action) => {
  switch (action.type) {
    case "ADD":
      return { items: [...state.items, action.item] };
    case "REMOVE":
      return { items: state.items.filter((_, i) => i !== action.index) };
    case "CLEAR":
      return { items: [] };
    default:
      return state;
  }
};

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

function StarRating({ rating, reviewCount, label = "Rating" }) {
  if (!rating) return null;
  const full = Math.floor(rating);
  const ratingFixed = parseFloat(rating).toFixed(1);
  const a11yLabel = reviewCount
    ? `${label}: ${ratingFixed} out of 5, ${reviewCount} reviews`
    : `${label}: ${ratingFixed} out of 5`;
  return (
    <HStack space={0.5} alignItems="center" accessibilityLabel={a11yLabel} accessibilityRole="text">
      <Text fontSize="xs" color="coolGray.700" minW="96px" aria-hidden="true">
        {label}
      </Text>
      {Array.from({ length: 5 }, (_, i) => (
        <Text
          key={i}
          fontSize="lg"
          color={i < full ? "amber.400" : "coolGray.300"}
          aria-hidden="true"
        >
          ★
        </Text>
      ))}
      <Text fontSize="sm" color="coolGray.600" ml={1} aria-hidden="true">
        {ratingFixed}
      </Text>
      {reviewCount ? (
        <Text fontSize="xs" color="coolGray.600" aria-hidden="true">
          ({reviewCount} reviews)
        </Text>
      ) : null}
    </HStack>
  );
}

// Accessibility rating (bizAccessRating, 1–5), as shown in the app header.
function AccessibilityRating({ value }) {
  const n = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
  if (!n) return null;
  return (
    <HStack
      space={0.5}
      alignItems="center"
      accessibilityLabel={`Accessibility rating: ${n} out of 5`}
      accessibilityRole="text"
    >
      <Text fontSize="xs" color="coolGray.700" minW="96px" aria-hidden="true">
        Accessibility
      </Text>
      {Array.from({ length: 5 }, (_, i) => (
        <Text key={i} fontSize="md" color={i < n ? "blue.600" : "coolGray.300"} aria-hidden="true">
          ♿
        </Text>
      ))}
      <Text fontSize="sm" color="coolGray.600" ml={1} aria-hidden="true">
        {`${n}/5`}
      </Text>
    </HStack>
  );
}

// mode: "order" (web ordering on) | "view" (read-only details) | undefined (not tappable)
function DishCard({ dish, onPress, mode = "order" }) {
  const dishImage = dish.DishImage || dish.dishPic || dish.dishImage || null;
  const sizes = dish.dishAvailSize || [];
  const firstPrice = sizes[0]?.price;
  const priceText =
    firstPrice != null ? `$${parseFloat(firstPrice).toFixed(2)}` : null;
  const hasAction = typeof onPress === "function";
  const hasOptions = (dish.dishOptions || []).some((g) => (g.optionItems || []).length > 0);
  const hasDetails = hasDishAccessibility(dish.accessibility);
  // Only present the dish as an add button when tapping actually adds it.
  const isOrderable = hasAction && mode === "order" && sizes.length > 0;
  const isViewable = hasAction && mode === "view";
  const actionLabel = isOrderable
    ? `Add ${dish.dishName} to cart`
    : isViewable
    ? `${dish.dishName}: view options, prices and details`
    : undefined;

  return (
    <Pressable
      onPress={isOrderable || isViewable ? onPress : undefined}
      accessibilityRole={isOrderable || isViewable ? "button" : undefined}
      accessibilityLabel={actionLabel}
      _pressed={{ opacity: 0.7 }}
    >
      <HStack
        space={3}
        py={3}
        borderBottomWidth={1}
        borderBottomColor="coolGray.100"
        role="listitem"
      >
        {/* Thumbnail */}
        <Box
          w="80px"
          h="65px"
          borderRadius="sm"
          overflow="hidden"
          flexShrink={0}
          bg="coolGray.100"
          justifyContent="center"
          alignItems="center"
        >
          {dishImage ? (
            <Image
              source={{ uri: dishImage }}
              alt={dish.dishName}
              w="100%"
              h="100%"
              resizeMode="cover"
            />
          ) : (
            <Text fontSize="2xl" aria-hidden="true">
              🍽️
            </Text>
          )}
        </Box>

        {/* Info */}
        <VStack flex={1} space={0.5} justifyContent="center">
          <HStack justifyContent="space-between" alignItems="flex-start">
            <Text
              fontWeight="semibold"
              color="coolGray.900"
              flex={1}
              mr={2}
              fontSize="sm"
            >
              {dish.dishName}
            </Text>
            {priceText && (
              <Text
                fontWeight="bold"
                color="red.500"
                fontSize="sm"
                accessibilityLabel={`Price: ${priceText}`}
              >
                {priceText}
              </Text>
            )}
          </HStack>

          {dish.dishDesc ? (
            <Text fontSize="xs" color="coolGray.600" numberOfLines={2}>
              {dish.dishDesc}
            </Text>
          ) : null}

          {/* Multiple sizes */}
          {sizes.length > 1 && (
            <HStack space={2} flexWrap="wrap" mt={0.5}>
              {sizes.map((s, i) => (
                <Text key={i} fontSize="xs" color="coolGray.600">
                  {s.size}: ${parseFloat(s.price).toFixed(2)}
                </Text>
              ))}
            </HStack>
          )}

          {isOrderable && (
            <Text fontSize="xs" color="red.400" mt={0.5} aria-hidden="true">
              Add to order +
            </Text>
          )}
          {isViewable && (hasOptions || hasDetails) && (
            <Text fontSize="xs" color="coolGray.600" mt={0.5} aria-hidden="true">
              {hasOptions && hasDetails ? "Options & dish details ›" : hasOptions ? "Options available ›" : "Dish details ›"}
            </Text>
          )}
        </VStack>
      </HStack>
    </Pressable>
  );
}

// Today's hours + open/closed (same information as the app), with the week below.
function OpeningHours({ venue }) {
  const status = getOpeningStatus(venue);
  const week = getWeeklyHours(venue);
  if (!status.known && week.length === 0 && !status.eventDate) return null;

  const stateText = !status.known ? null : status.isOpen ? "Open now" : "Closed";
  const summary = [
    status.eventDate,
    stateText,
    status.known ? status.todayText : "Hours not available today",
    !status.isOpen ? status.nextOpenText : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box mb={4}>
      <Heading size="xs" color="coolGray.800" accessibilityRole="header" aria-level="2" mb={1}>
        Opening hours
      </Heading>
      <Text
        fontSize="sm"
        color={!status.known ? "coolGray.700" : status.isOpen ? "green.700" : "red.700"}
        fontWeight="semibold"
      >
        {summary}
      </Text>
      {week.length > 0 && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ cursor: "pointer", fontSize: 14, color: "#374151" }}>All opening hours</summary>
          <table style={{ fontSize: 14, color: "#374151", marginTop: 4, borderCollapse: "collapse" }}>
            <caption style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
              {`Opening hours for ${venue.bizName}`}
            </caption>
            <tbody>
              {week.map((d) => (
                <tr key={d.day} style={{ fontWeight: d.isToday ? 700 : 400 }}>
                  <th scope="row" style={{ textAlign: "left", paddingRight: 16, fontWeight: "inherit" }}>
                    {d.day}{d.isToday ? " (today)" : ""}
                  </th>
                  <td>{d.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </Box>
  );
}

// Floating cart bar at bottom of page
function CartBar({ itemCount, total, onOpen }) {
  if (itemCount === 0) return null;
  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={100}
      safeAreaBottom
      bg="red.600"
      px={5}
      py={3}
      shadow={6}
    >
      <Pressable
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityLabel={`View cart: ${itemCount} item${itemCount !== 1 ? "s" : ""}, $${total.toFixed(2)}`}
      >
        <HStack justifyContent="space-between" alignItems="center">
          <HStack space={2} alignItems="center">
            <Box
              bg="white"
              borderRadius="full"
              w={7}
              h={7}
              justifyContent="center"
              alignItems="center"
            >
              <Text fontWeight="bold" color="red.600" fontSize="sm">
                {itemCount}
              </Text>
            </Box>
            <Text color="white" fontWeight="bold" fontSize="md">
              View cart
            </Text>
          </HStack>
          <Text color="white" fontWeight="bold" fontSize="md">
            ${total.toFixed(2)}
          </Text>
        </HStack>
      </Pressable>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────

const VenuePage = () => {
  const { bizId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeDish, setActiveDish] = useState(null); // dish being customised
  const [cartOpen, setCartOpen] = useState(false);

  // Parse ?tableNo=N (or legacy ?table=N) from URL (set when opening from a QR code)
  const tableNoFromUrl = new URLSearchParams(location.search).get("tableNo") || new URLSearchParams(location.search).get("table") || "";

  const [cart, dispatch] = useReducer(cartReducer, { items: [] });
  const [cartAnnouncement, setCartAnnouncement] = useState("");
  const cartItemCount = cart.items.reduce((n, i) => n + i.quantity, 0);
  const cartTotal = cart.items.reduce((s, i) => s + i.lineTotal, 0);

  const venueKey  = `vp_venue_${bizId}`;
  const aliasKey  = `vp_venueAlias_${bizId}`;
  const menusKey  = `vp_menus_${bizId}`;
  const dishesKey = `vp_dishes_${bizId}`;
  const menuAccKey = `vp_menuAcc_${bizId}`;

  const venue = useSelector(({ firestore: { data } }) => data[venueKey]);
  // The route ID isn't a Business doc ID (e.g. a google_… ID the app uses,
  // which lives in the bizId field): look the venue up by that field instead.
  const needsAlias = isLoaded(venue) && !venue;
  const aliasMatches = useSelector(({ firestore: { ordered } }) => ordered[aliasKey]);
  const aliasDocId = needsAlias && aliasMatches?.[0]?.id;

  useFirestoreConnect([
    { collection: "Business", doc: bizId, storeAs: venueKey },
    ...(needsAlias
      ? [{ collection: "Business", where: [["bizId", "==", bizId]], limit: 1, storeAs: aliasKey }]
      : []),
    // No orderBy: it drops menus without createdOn (e.g. Square imports).
    // Menus are sorted client-side with sortMenus().
    {
      collection: "MenuDetails",
      where: [["menuBizId", "==", bizId]],
      storeAs: menusKey,
    },
    {
      collection: "DishDetails",
      where: [["bizId", "==", bizId]],
      storeAs: dishesKey,
    },
    // Allergen/dish accessibility for POS-imported menus (publicly readable).
    // Optional: the page renders without it.
    {
      collection: "Business",
      doc: bizId,
      subcollections: [{ collection: "menu_accessibility" }],
      storeAs: menuAccKey,
    },
  ]);

  // Found by the bizId field: switch to the canonical doc-ID URL (keeping
  // ?tableNo etc.) so menus, ordering and links all use the doc ID.
  useEffect(() => {
    if (aliasDocId && aliasDocId !== bizId) {
      history.replace(`/venues/${aliasDocId}${location.search}${location.hash}`);
    }
  }, [aliasDocId, bizId, history, location.search, location.hash]);

  const menusRaw  = useSelector(({ firestore: { ordered } }) => ordered[menusKey]);
  const dishesRaw = useSelector(({ firestore: { ordered } }) => ordered[dishesKey]);
  const menus  = sortMenus(menusRaw);
  const menuAccRaw = useSelector(({ firestore: { ordered } }) => ordered[menuAccKey]);
  const dishes = attachMenuAccessibility(dishesRaw || [], menuAccRaw);

  const resolvingAlias = needsAlias && (!isLoaded(aliasMatches) || !!aliasDocId);
  const loaded = isLoaded(venue) && isLoaded(menusRaw) && isLoaded(dishesRaw) && !resolvingAlias;

  if (!loaded) {
    return (
      <Center flex={1} minH="60vh" role="status" aria-live="polite">
        <Spinner size="lg" color="red.500" accessibilityLabel="Loading venue" />
      </Center>
    );
  }

  if (!venue) {
    return (
      <Center flex={1} minH="60vh">
        <Heading size="md" color="coolGray.600">
          Venue not found
        </Heading>
      </Center>
    );
  }

  // ── Derived data ──────────────────────────────────────────────────

  const images = [];
  for (let i = 1; i <= 5; i++) {
    if (venue[`bizPic${i}`]) {
      images.push({
        url: venue[`bizPic${i}`],
        alt: venue[`bizPic${i}Alt`] || `Photo ${i} of ${venue.bizName}`,
      });
    }
  }

  // A dish is visible if:
  //   - dishOn is explicitly true, OR
  //   - dishOn is missing/undefined (legacy, no flag set), OR
  //   - requiresVerification is true (scraped/imported — dishOn defaults to false
  //     but the item came from the venue's own website so show it as-is)
  const isDishVisible = (d) => d.dishOn === true || d.dishOn == null || d.requiresVerification === true;
  const isMenuVisible = (m) => m.menuOn === true || m.menuOn == null || m.requiresVerification === true;

  const dishesByMenu = {};
  dishes
    .filter(isDishVisible)
    .forEach((dish) => {
      // Mobile-app dishes store the menu ref in `menuID` (UUID).
      // Scraper-imported dishes store it in `dishMenuId` (Firestore doc ID).
      const mid = dish.menuID || dish.menuId || dish.dishMenuId;
      if (!dishesByMenu[mid]) dishesByMenu[mid] = [];
      dishesByMenu[mid].push(dish);
    });

  // Like the app: menus that are switched off or outside their times are still
  // shown, marked "(currently off)", after the available ones (G10).
  // Scraped/imported menus (requiresVerification) default to menuOn:false but
  // are shown as normal, as before.
  const activeMenus = orderMenusForDisplay(
    menus.map((m) => (isMenuVisible(m) && m.menuOn === false ? { ...m, menuOn: true } : m)),
    venue
  );
  const isUnclaimed = venue.claimStatus !== "claimed";
  const rating = venue.bizRating || venue.googleRating;
  const hasMenu = activeMenus.some(
    (m) => (dishesByMenu[m.menuId || m.id] || []).length > 0
  );
  // Web ordering is switched off (see orderingConfig); venues the app can take
  // orders for show "Order through the Piatto app" instead of the cart.
  const isOrderable = WEB_ORDERING_ENABLED && hasMenu && canTakePayments(venue);
  // Taking orders through Piatto = can take payments (Stripe account with
  // charges enabled). See canTakePayments in orderingConfig.
  const isPiattoPartner = canTakePayments(venue);
  const showOrderInApp = !WEB_ORDERING_ENABLED && hasMenu && isPiattoPartner;

  // ── SEO meta & schema.org JSON-LD ────────────────────────────────
  const cuisineNames = (venue.bizCuisines || []).slice(0, 5);
  const venueDescription = venue.bizEditorialSummary
    || `${venue.bizName} in ${venue.bizSuburb || venue.bizAddress || "Australia"}${cuisineNames.length ? ` — ${cuisineNames.join(", ")}` : ""}. View menu, hours, accessibility info and order on Piatto.`;

  const priceMap = { 1: "$", 2: "$$", 3: "$$$", 4: "$$$$" };

  // Build opening hours in schema.org format from day-specific fields
  const dayFields = [
    { day: "Monday", open: venue.bizMondayOpen, close: venue.bizMondayClose },
    { day: "Tuesday", open: venue.bizTuesdayOpen, close: venue.bizTuesdayClose },
    { day: "Wednesday", open: venue.bizWednesdayOpen, close: venue.bizWednesdayClose },
    { day: "Thursday", open: venue.bizThursdayOpen, close: venue.bizThursdayClose },
    { day: "Friday", open: venue.bizFridayOpen, close: venue.bizFridayClose },
    { day: "Saturday", open: venue.bizSaturdayOpen, close: venue.bizSaturdayClose },
    { day: "Sunday", open: venue.bizSundayOpen, close: venue.bizSundayClose },
  ];
  const openingHoursSpec = dayFields
    .filter((d) => d.open && d.close)
    .map((d) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: d.day,
      opens: d.open,
      closes: d.close,
    }));

  const schema = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: venue.bizName,
    description: venueDescription,
    address: {
      "@type": "PostalAddress",
      streetAddress: venue.bizAddress,
      addressLocality: venue.bizSuburb,
      addressRegion: venue.bizState,
      addressCountry: "AU",
      ...(venue.bizPostalCode ? { postalCode: venue.bizPostalCode } : {}),
    },
    ...(venue.bizPhone    ? { telephone: venue.bizPhone }    : {}),
    ...(venue.bizWeb      ? { url: venue.bizWeb }            : {}),
    ...(images.length > 0 ? { image: images.map((i) => i.url) } : {}),
    ...(cuisineNames.length > 0 ? { servesCuisine: cuisineNames } : {}),
    ...(venue.bizAveragePrice ? { priceRange: priceMap[venue.bizAveragePrice] || "$$" } : {}),
    ...(openingHoursSpec.length > 0 ? { openingHoursSpecification: openingHoursSpec } : {}),
    ...(venue.bizLat && venue.bizLon
      ? { geo: { "@type": "GeoCoordinates", latitude: venue.bizLat, longitude: venue.bizLon } }
      : {}),
    ...(venue.bizDineIn != null ? { "amenityFeature": [{ "@type": "LocationFeatureSpecification", name: "Dine-in", value: venue.bizDineIn }] } : {}),
    ...(rating
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: rating,
            bestRating: 5,
            reviewCount: venue.googleReviewCount || 1,
          },
        }
      : {}),
    ...(hasMenu ? { hasMenu: { "@type": "Menu", url: `https://piatto.com.au/venues/${bizId}` } } : {}),
  };

  const canonicalUrl = `https://piatto.com.au/venues/${bizId}`;

  // ── Render ─────────────────────────────────────────────────────
  return (
    <Box bg="white" minH="100vh" pb={cartItemCount > 0 ? "80px" : 0} role="main">
      {hasMenu && (
        <a
          href="#venue-menu"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            whiteSpace: "nowrap",
          }}
          onFocus={(e) => Object.assign(e.target.style, { position: "static", width: "auto", height: "auto", overflow: "visible", clip: "auto", whiteSpace: "normal", display: "block", padding: "8px 16px", background: "#B91C1C", color: "white", textAlign: "center", fontWeight: "bold", textDecoration: "none" })}
          onBlur={(e) => Object.assign(e.target.style, { position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" })}
        >
          Skip to menu
        </a>
      )}
      <SEO
        title={`${venue.bizName}${venue.bizSuburb ? ` — ${venue.bizSuburb}` : ""}`}
        description={venueDescription}
        image={images.length > 0 ? images[0].url : undefined}
        url={canonicalUrl}
        type="restaurant"
        schema={schema}
      />

      {/* Hero image + thumbnail strip */}
      {images.length > 0 && (
        <Box>
          <Box
            w="100%"
            h={{ base: "220px", md: "400px" }}
            bg="coolGray.100"
            overflow="hidden"
          >
            <Image
              source={{ uri: images[selectedImage].url }}
              alt={images[selectedImage].alt}
              w="100%"
              h="100%"
              resizeMode="cover"
            />
          </Box>

          {images.length > 1 && (
            <HStack space={1} px={3} py={2} bg="coolGray.50">
              {images.map((img, i) => (
                <Pressable
                  key={i}
                  onPress={() => setSelectedImage(i)}
                  accessibilityLabel={`View image: ${img.alt}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected: i === selectedImage }}
                >
                  <Box
                    w="64px"
                    h="48px"
                    borderRadius="sm"
                    overflow="hidden"
                    borderWidth={2}
                    borderColor={i === selectedImage ? "red.500" : "transparent"}
                  >
                    <Image
                      source={{ uri: img.url }}
                      alt={img.alt}
                      w="100%"
                      h="100%"
                      resizeMode="cover"
                    />
                  </Box>
                </Pressable>
              ))}
            </HStack>
          )}
        </Box>
      )}

      {/* Page body */}
      <Box px={{ base: 4, md: 8 }} py={5} maxW="900px" mx="auto" w="100%">

        {/* Claim banner */}
        {isUnclaimed && (
          <Box
            bg="amber.50"
            borderWidth={1}
            borderColor="amber.300"
            borderRadius="md"
            px={4}
            py={3}
            mb={5}
            role="complementary"
            aria-label="Claim this venue"
          >
            <HStack
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              space={2}
            >
              <VStack flex={1}>
                <Text fontWeight="bold" color="amber.800">
                  Is this your venue?
                </Text>
                <Text fontSize="sm" color="amber.700">
                  Claim it free to update your menu, photos and details.
                </Text>
              </VStack>
              <Button
                size="sm"
                bg="red.600"
                _text={{ color: "white", fontWeight: "bold" }}
                onPress={() => (window.location.href = `/claim-venue/${bizId}`)}
                accessibilityLabel="Claim this venue for free"
              >
                Claim Free →
              </Button>
            </HStack>
          </Box>
        )}

        {/* Venue name + cuisine */}
        <VStack space={1} mb={3}>
          <Heading size="xl" color="coolGray.900" accessibilityRole="header" aria-level="1">
            {venue.bizName}
          </Heading>
          {isPiattoPartner && (
            <HStack>
              <Badge
                colorScheme="success"
                variant="subtle"
                accessibilityLabel="Piatto Partner — this venue accepts orders through the Piatto app"
              >
                Piatto partner ✓
              </Badge>
            </HStack>
          )}
          {(venue.bizType?.typeName ||
            (venue.bizCuisines && venue.bizCuisines.length > 0)) && (
            <Text fontSize="sm" color="coolGray.600">
              {[
                venue.bizType?.typeName,
                ...(venue.bizCuisines || []).slice(0, 3),
              ]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          )}
          {estimatedPriceText(venue) && (
            <Text fontSize="sm" color="coolGray.600">
              {`Est price: ${estimatedPriceText(venue)}`}
            </Text>
          )}
        </VStack>

        {(venue.googleRating || venue.bizRating > 0 || venue.bizAccessRating) && (
          <VStack space={1} mb={4}>
            {/* bizRating is Google's rating copied at import (venueImport.ts), not a
                Piatto rating, so show one star rating: Google's with its count, else
                bizRating as a plain "Rating" (as the app does). */}
            {venue.googleRating ? (
              <StarRating rating={venue.googleRating} reviewCount={venue.googleReviewCount} label="Google rating" />
            ) : venue.bizRating > 0 ? (
              <StarRating rating={venue.bizRating} label="Rating" />
            ) : null}
            <AccessibilityRating value={venue.bizAccessRating} />
          </VStack>
        )}

        <OpeningHours venue={venue} />

        <Divider mb={4} />

        {/* Address + action buttons */}
        <VStack space={3} mb={5}>
          {venue.bizAddress && (
            <HStack space={2} alignItems="flex-start">
              <Text color="red.500" fontSize="md" aria-hidden="true">
                📍
              </Text>
              <Text
                color="coolGray.700"
                flex={1}
                accessibilityLabel={`Address: ${venue.bizAddress}`}
              >
                {venue.bizAddress}
                {venue.bizSuburb ? `, ${venue.bizSuburb}` : ""}
                {venue.bizState ? ` ${venue.bizState}` : ""}
              </Text>
            </HStack>
          )}

          <HStack space={2} flexWrap="wrap">
            {venue.bizPhone && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="coolGray"
                onPress={() => window.open(`tel:${venue.bizPhone}`)}
                accessibilityLabel={`Call ${venue.bizName}`}
                mb={1}
              >
                📞 Call
              </Button>
            )}
            {venue.bizWeb && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="coolGray"
                onPress={() =>
                  window.open(venue.bizWeb, "_blank", "noopener,noreferrer")
                }
                accessibilityLabel={`Visit ${venue.bizName} website`}
                mb={1}
              >
                🌐 Website
              </Button>
            )}
            {venue.bizLat && venue.bizLon && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="coolGray"
                onPress={() =>
                  window.open(
                    `https://maps.google.com/?q=${venue.bizLat},${venue.bizLon}`,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                accessibilityLabel={`Get directions to ${venue.bizName}`}
                mb={1}
              >
                🗺️ Directions
              </Button>
            )}
          </HStack>
        </VStack>

        {/* Venue details: same content as the app's VenueDetailsSection */}
        <VenueDetails venue={venue} />

        {/* Accessibility details: same summary as the app, then venue-entered details */}
        <VStack space={2} mb={4} role="region" aria-label="Accessibility details">
          <HStack space={2} alignItems="center" flexWrap="wrap">
            <Heading size="sm" color="coolGray.800" accessibilityRole="header" aria-level="2">
              Accessibility details
            </Heading>
            <Badge variant="outline" colorScheme="coolGray">
              {CONFIDENCE_LABELS[overallConfidence(venue)].label}
            </Badge>
          </HStack>

          <Text fontSize="xs" color="coolGray.600">
            Summary from Google and Piatto data. Confirm details with the venue if you need to.
          </Text>

          {venue.aiAccessNarrative ? (
            <Text color="coolGray.700" fontSize="sm" italic>
              {`“${venue.aiAccessNarrative}”`}
            </Text>
          ) : (
            <Text color="coolGray.600" fontSize="sm">
              No accessibility review yet.
            </Text>
          )}

          <VStack space={2}>
            {buildAccessibilityCategories(venue).map((cat) => (
              <VStack key={cat.key} space={0.5}>
                <HStack space={2} alignItems="center">
                  <Text fontWeight="semibold" color="coolGray.800" fontSize="sm" accessibilityRole="header" aria-level="3">
                    {cat.label}
                  </Text>
                  <Text fontSize="xs" color="coolGray.600">
                    {`(${CONFIDENCE_LABELS[cat.confidence].short})`}
                  </Text>
                </HStack>
                {cat.confidence === 1 ? (
                  <Text fontSize="sm" color="coolGray.600">
                    No data available
                  </Text>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {cat.features.map((f) => (
                      <li key={f.label} style={{ fontSize: 14, color: "#374151" }}>
                        {f.informational ? f.label : `${f.label}: ${STATUS_LABELS[f.status]}`}
                        {f.details ? ` (${f.details})` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </VStack>
            ))}
          </VStack>

        {hasVenueEnteredAccessibility(venue) && (
          <VStack space={2} mt={2}>
            <Text fontWeight="semibold" color="coolGray.800" fontSize="sm" accessibilityRole="header" aria-level="3">
              From the venue
            </Text>

            {venue.bizDisEntrance ? (
              <VStack space={1}>
                <Text fontWeight="semibold" color="coolGray.800" fontSize="sm" accessibilityRole="header" aria-level="4">
                  Getting in
                </Text>
                <Text color="coolGray.700" fontSize="sm">
                  {venue.bizDisEntrance}
                </Text>
              </VStack>
            ) : null}

            {venue.bizDisBathType && venue.bizDisBathType !== "none" && (
              <>
                <HStack space={2} alignItems="center" flexWrap="wrap">
                  <Text color="coolGray.700" fontSize="sm" aria-hidden="true">♿</Text>
                  <Text color="coolGray.700" fontSize="sm"
                    accessibilityLabel={`Accessible bathroom: ${
                      venue.bizDisBathType === "accessible_as1428" ? "AS 1428.1 wheelchair-accessible"
                      : venue.bizDisBathType === "ambulant" ? "Ambulant"
                      : venue.bizDisBathType === "changing_places" ? "Changing Places"
                      : venue.bizDisBathType
                    }`}
                  >
                    {venue.bizDisBathType === "accessible_as1428" && "Accessible Bathroom (AS 1428.1)"}
                    {venue.bizDisBathType === "ambulant" && "Ambulant Bathroom"}
                    {venue.bizDisBathType === "changing_places" && "Changing Places Bathroom"}
                  </Text>
                </HStack>
                {venue.bizDisBathFeatures && venue.bizDisBathFeatures.length > 0 && (
                  <HStack space={1} flexWrap="wrap" role="list" aria-label="Bathroom features">
                    {venue.bizDisBathFeatures.map((f) => {
                      const labels = {
                        grab_rails: "Grab Rails", transfer_space_left: "Transfer Space (Left)",
                        transfer_space_right: "Transfer Space (Right)", transfer_space_both: "Transfer Space (Both)",
                        emergency_call: "Emergency Call", step_free_access: "Step-Free Access",
                        unisex: "Unisex", baby_change: "Baby Change", adult_change_table: "Adult Change Table",
                        ceiling_hoist: "Ceiling Hoist",
                      };
                      return (
                        <Badge key={f} colorScheme="green" variant="subtle" mb={1} role="listitem">
                          {labels[f] || f}
                        </Badge>
                      );
                    })}
                  </HStack>
                )}
              </>
            )}

            {venue.bizDisBathDir ? (
              <VStack space={1}>
                <Text fontWeight="semibold" color="coolGray.800" fontSize="sm" accessibilityRole="header" aria-level="4">
                  Finding the accessible bathroom
                </Text>
                <Text color="coolGray.700" fontSize="sm">
                  {venue.bizDisBathDir}
                </Text>
              </VStack>
            ) : null}

            {venue.bizDisStdBathDir ? (
              <VStack space={1}>
                <Text fontWeight="semibold" color="coolGray.800" fontSize="sm" accessibilityRole="header" aria-level="4">
                  Finding the bathrooms
                </Text>
                <Text color="coolGray.700" fontSize="sm">
                  {venue.bizDisStdBathDir}
                </Text>
              </VStack>
            ) : null}
          </VStack>
        )}
        </VStack>

        <Divider mb={5} />

        {showOrderInApp && (
          <OrderInApp venue={venue} docId={bizId} tableNo={tableNoFromUrl} />
        )}

        {!isPiattoPartner && (
          <Box
            bg="coolGray.50"
            borderWidth={1}
            borderColor="coolGray.200"
            borderRadius="lg"
            p={4}
            mb={6}
          >
            <Text fontWeight="semibold" color="coolGray.800" mb={1}>
              {`${venue.bizName} isn't taking orders through Piatto yet`}
            </Text>
            <Text fontSize="sm" color="coolGray.700">
              {`This venue is not yet a Piatto Partner, so ordering isn't available. You can still view ${
                hasMenu ? "its menu, " : ""
              }opening hours and accessibility information here and in the Piatto app.`}
            </Text>
          </Box>
        )}

        {/* Menu sections */}
        {activeMenus.length > 0 ? (
          <VStack space={7} mb={6}>
            <HStack justifyContent="space-between" alignItems="center">
              <Heading size="md" color="coolGray.800" accessibilityRole="header" aria-level="2" nativeID="venue-menu" tabIndex={-1}>
                Menu
              </Heading>
              {isOrderable && (
                <Text fontSize="xs" color="coolGray.600">
                  Select a dish to add it
                </Text>
              )}
            </HStack>

            {activeMenus.map((menu) => {
              const menuDishes = dishesByMenu[menu.menuId || menu.id] || [];
              if (menuDishes.length === 0) return null;

              return (
                <VStack key={menu.menuId || menu.id} space={0} opacity={menu._isOff ? 0.6 : 1}>
                  <HStack
                    alignItems="center"
                    space={2}
                    mb={1}
                  >
                    <Heading size="sm" color="coolGray.800" accessibilityRole="header" aria-level="3">
                      {menu.menuName}
                      {menu._isOff ? " (currently off)" : ""}
                    </Heading>
                    <Badge
                      colorScheme={
                        menu.menuType === "Food"
                          ? "red"
                          : menu.menuType === "Drinks"
                          ? "blue"
                          : "gray"
                      }
                      variant="subtle"
                    >
                      {menu.menuType}
                    </Badge>
                  </HStack>

                  {menu.menuDesc ? (
                    <Text fontSize="xs" color="coolGray.600" mb={2}>
                      {menu.menuDesc}
                    </Text>
                  ) : null}

                  {(menu.menuOnTime || menu.menuOffTime) && (
                    <Text fontSize="xs" color="coolGray.600" mb={2}>
                      Available {menu.menuOnTime} – {menu.menuOffTime}
                    </Text>
                  )}

                  <Box role="list" aria-label={`${menu.menuName} dishes`}>
                    {menuDishes.map((dish) => (
                      <DishCard
                        key={dish.dishID || dish.id}
                        dish={dish}
                        mode={isOrderable ? "order" : "view"}
                        onPress={
                          isOrderable || !WEB_ORDERING_ENABLED
                            ? () => setActiveDish(dish)
                            : undefined
                        }
                      />
                    ))}
                  </Box>
                </VStack>
              );
            })}
          </VStack>
        ) : (
          <Box py={6} alignItems="center" mb={6}>
            <Text color="coolGray.600" fontSize="sm" textAlign="center">
              No menu available yet.
              {isUnclaimed
                ? " Claim this venue to add your menu."
                : " Check back soon."}
            </Text>
          </Box>
        )}

        <Divider mb={6} />

        {/* Download app CTA */}
        <Box
          bg="coolGray.900"
          borderRadius="xl"
          px={6}
          py={6}
          mb={10}
          alignItems="center"
          role="complementary"
          aria-label="Download the Piatto app"
        >
          <Text
            color="white"
            fontWeight="bold"
            fontSize="lg"
            textAlign="center"
            mb={1}
          >
            Order from {venue.bizName}
          </Text>
          <Text
            color="coolGray.400"
            fontSize="sm"
            textAlign="center"
            mb={4}
          >
            Download the free Piatto app to order, reserve a table and
            discover accessible dining near you.
          </Text>
          <HStack space={3}>
            <Button
              size="sm"
              bg="white"
              _text={{ color: "coolGray.900", fontWeight: "bold" }}
              onPress={() =>
                window.open(
                  "https://apps.apple.com/au/app/piatto/id1602642481",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              accessibilityLabel="Download Piatto on the App Store"
            >
              App Store
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor="white"
              _text={{ color: "white", fontWeight: "bold" }}
              onPress={() =>
                window.open(
                  "https://play.google.com/store/apps/details?id=com.cubage.winndine",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              accessibilityLabel="Get Piatto on Google Play"
            >
              Google Play
            </Button>
          </HStack>
        </Box>

      </Box>

      {/* Screen reader cart announcement */}
      <div
        aria-live="polite"
        role="status"
        style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }}
      >
        {cartAnnouncement}
      </div>

      {/* Floating cart bar */}
      {WEB_ORDERING_ENABLED && (
        <CartBar
          itemCount={cartItemCount}
          total={cartTotal}
          onOpen={() => setCartOpen(true)}
        />
      )}

      {/* Read-only dish details while web ordering is off */}
      {!WEB_ORDERING_ENABLED && activeDish && (
        <DishModal
          dish={activeDish}
          readOnly
          onClose={() => setActiveDish(null)}
        />
      )}

      {/* Dish customisation modal */}
      {WEB_ORDERING_ENABLED && activeDish && (
        <DishModal
          dish={activeDish}
          onClose={() => setActiveDish(null)}
          onAddToCart={(item) => {
            dispatch({ type: "ADD", item });
            setActiveDish(null);
            const newCount = cartItemCount + item.quantity;
            const newTotal = cartTotal + item.lineTotal;
            setCartAnnouncement(`${item.dish.dishName} added to cart. Cart total: ${newCount} item${newCount !== 1 ? "s" : ""}, $${newTotal.toFixed(2)}.`);
          }}
        />
      )}

      {/* Cart / checkout sheet */}
      {WEB_ORDERING_ENABLED && cartOpen && (
        <CartSheet
          items={cart.items}
          venue={venue}
          bizId={bizId}
          tableNo={tableNoFromUrl}
          onClose={() => setCartOpen(false)}
          onRemove={(index) => dispatch({ type: "REMOVE", index })}
          onClearCart={() => dispatch({ type: "CLEAR" })}
        />
      )}
    </Box>
  );
};

export default VenuePage;
